import type { Note, NoteUpdateInput, NoteVersion, Category, Reminder } from '@voice/shared';
import { supabase } from './supabase';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3001';

async function getAuthHeaders(): Promise<Record<string, string>> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }
  return {
    Authorization: `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
  };
}

export interface CreateNoteInput {
  id?: string;
  audio_url: string;
  duration_seconds: number;
}

export async function createNote(input: CreateNoteInput): Promise<Note> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE}/api/notes`, {
    method: 'POST',
    headers,
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? `API error ${res.status}`);
  }
  const data = (await res.json()) as { note: Note };
  return data.note;
}

export async function fetchNote(noteId: string): Promise<Note> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE}/api/notes/${noteId}`, {
    method: 'GET',
    headers,
  });
  if (!res.ok) {
    throw new Error(`API error ${res.status}`);
  }
  const data = (await res.json()) as { note: Note };
  return data.note;
}

export async function updateNote(noteId: string, updates: NoteUpdateInput): Promise<Note> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE}/api/notes/${noteId}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(updates),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? `API error ${res.status}`);
  }
  const data = (await res.json()) as { note: Note };
  return data.note;
}

export async function reprocessNote(noteId: string): Promise<Note> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE}/api/notes/${noteId}/reprocess`, {
    method: 'POST',
    headers,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? `API error ${res.status}`);
  }
  const data = (await res.json()) as { note: Note };
  return data.note;
}

export async function fetchNoteVersions(noteId: string): Promise<NoteVersion[]> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE}/api/notes/${noteId}/versions`, {
    method: 'GET',
    headers,
  });
  if (!res.ok) {
    throw new Error(`API error ${res.status}`);
  }
  const data = (await res.json()) as { versions: NoteVersion[] };
  return data.versions;
}

// ── Categories ───────────────────────────────────────────────

export interface UpdateCategoryInput {
  name?: string;
  color?: string | null;
  approved?: boolean;
  parent_id?: string | null;
}

export async function updateCategory(
  categoryId: string,
  updates: UpdateCategoryInput
): Promise<Category> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE}/api/categories/${categoryId}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(updates),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? `API error ${res.status}`);
  }
  const data = (await res.json()) as { category: Category };
  return data.category;
}

// ── Reminders ────────────────────────────────────────────────

export interface ReminderWithNote extends Reminder {
  notes: { id: string; summary: string | null; title: string | null };
}

export async function fetchReminders(): Promise<ReminderWithNote[]> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE}/api/reminders`, {
    method: 'GET',
    headers,
  });
  if (!res.ok) {
    throw new Error(`API error ${res.status}`);
  }
  const data = (await res.json()) as { reminders: ReminderWithNote[] };
  return data.reminders;
}

// ── AI Approval / Undo ──────────────────────────────────────

export interface ApproveAIInput {
  intent: 'note' | 'task' | 'reminder';
  category_id: string | null;
  approve_category: boolean;
  accepted_action_indices: number[];
}

export async function approveAI(noteId: string, input: ApproveAIInput): Promise<Note> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE}/api/notes/${noteId}/approve-ai`, {
    method: 'POST',
    headers,
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? `API error ${res.status}`);
  }
  const data = (await res.json()) as { note: Note };
  return data.note;
}

export async function undoAI(noteId: string): Promise<Note> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE}/api/notes/${noteId}/undo-ai`, {
    method: 'POST',
    headers,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? `API error ${res.status}`);
  }
  const data = (await res.json()) as { note: Note };
  return data.note;
}

// ── Delete ───────────────────────────────────────────────────

export async function deleteNote(noteId: string): Promise<void> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE}/api/notes/${noteId}`, {
    method: 'DELETE',
    headers,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? `API error ${res.status}`);
  }
}

// ── Profiles ────────────────────────────────────────────────

export interface ProfileData {
  id: string;
  google_calendar_sync: boolean;
  google_refresh_token: string | null;
}

export async function fetchProfile(): Promise<ProfileData> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE}/api/profiles/me`, {
    method: 'GET',
    headers,
  });
  if (!res.ok) {
    throw new Error(`API error ${res.status}`);
  }
  const data = (await res.json()) as { profile: ProfileData };
  return data.profile;
}

export async function updateProfile(
  updates: Partial<Pick<ProfileData, 'google_calendar_sync' | 'google_refresh_token'>>
): Promise<ProfileData> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE}/api/profiles/me`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(updates),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? `API error ${res.status}`);
  }
  const data = (await res.json()) as { profile: ProfileData };
  return data.profile;
}
